import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Injector, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { AppInsightsService, LibModalService } from '@experian-digital/affordability-library';
import { Application } from '@portal-shared/models/application';
import { Department } from '@portal-shared/models/department';
import { AuditEventsEnum } from '@portal-shared/models/enums/audit-events.enum';
import { DepartmentFeatures } from '@portal-shared/models/enums/department-features';
import { STORAGE_KEYS } from '@portal-shared/models/enums/storage-keys.enum';
import { ApplicationDetailsService } from '@portal-shared/services/application-details.service';
import { ApplicationConfigService } from '@portal-shared/services/config.service';
import { CustomerService } from '@portal-shared/services/customer.service';
import { DepartmentService } from '@portal-shared/services/department.service';
import { ErrorService } from '@portal-shared/services/error.service';
import { BackButton, Error, Loading, Modal, STORE_FIELDS, StoreState } from '@portal-store/models/store.model';
import { StoreService } from '@portal-store/store.service';
import { Observable, Subject, of } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { APP_ROUTES, currentRouteEquals } from '../../routes';

@Component({
  selector: 'aff-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.scss'],
  providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BaseComponent implements OnDestroy {
  store: StoreService;
  router: Router;
  modalService: LibModalService;
  departmentService: DepartmentService;
  customerService: CustomerService;
  errorService: ErrorService;
  applicationDetailsService: ApplicationDetailsService;

  loading$: Observable<Loading>;
  error$: Observable<Error>;
  backButton$: Observable<BackButton>;
  modal$: Observable<Modal> = of(undefined);
  subscribe$: Subject<any> = new Subject();

  department: Department;
  application: Application;
  departmentName: string;
  appRoutes = APP_ROUTES;
  currentRouteEquals = currentRouteEquals;

  constructor(private injector: Injector) {
    this.initialization(injector);
    this.getResolverData();
    this.addAppInsightsTelemetry();
    this.application = this.store.get(STORE_FIELDS.application);
  }

  get isCustomersFeatureOn(): boolean {
    return this.department?.features?.includes(DepartmentFeatures.PortalCustomers);
  }

  get isPepsAndSanctionsFeatureOn(): boolean {
    return this.department?.features?.includes(DepartmentFeatures.PepsAndSanctions);
  }

  get isWRFeatureOn(): boolean {
    return this.department?.features?.includes(DepartmentFeatures.WorkReport);
  }

  get isOBFeatureOn(): boolean {
    return this.department?.features?.includes(DepartmentFeatures.OpenBanking);
  }

  get isOBStatementFeatureOn(): boolean {
    return this.department?.features?.includes(DepartmentFeatures.OpenBankingStatementReport);
  }

  get isMonthlyOBStatementFeatureOn(): boolean {
    return this.department?.features?.includes(DepartmentFeatures.MonthlyOpenBankingStatement);
  }

  get isCoverageCheckerFeatureOn(): boolean {
    return this.department.features.includes(DepartmentFeatures.CoverageChecker);
  }

  get isJointRefCustom(): boolean {
    return this.department.features.includes(DepartmentFeatures.JointApplicationsMultipleDepartmentReference);
  }

  get isCreditReportPortalDirect(): boolean {
    return this.department?.features?.includes(DepartmentFeatures.CreditReportPortalDirect);
  }

  get isMultiAddress(): boolean {
    return this.isPepsAndSanctionsFeatureOn || this.isCreditReportPortalDirect;
  }

  get isIdDoc(): boolean {
    return this.department?.features?.includes(DepartmentFeatures.IdDocVerificationPassport);
  }

  isSingleFeatureOnly(feature: string): boolean {
    return this.applicationDetailsService.isSingleFeatureOnly(feature);
  }

  get isWorkReportHybridFeatureOn(): boolean {
    return this.department?.features?.includes(DepartmentFeatures.WorkReportDataViewer);
  }

  private initialization(injector: Injector): void {
    this.store = injector.get(StoreService);
    this.router = injector.get(Router);

    this.modalService = injector.get(LibModalService);
    this.departmentService = injector.get(DepartmentService);
    this.customerService = injector.get(CustomerService);
    this.errorService = injector.get(ErrorService);
    this.applicationDetailsService = injector.get(ApplicationDetailsService);

    this.loading$ = this.getStoreState<Loading>(STORE_FIELDS.loading);
    this.error$ = this.getStoreState<Error>(STORE_FIELDS.error);
    this.backButton$ = this.getStoreState<BackButton>(STORE_FIELDS.backButton);
    this.modal$ = this.getStoreState<Modal>(STORE_FIELDS.modal);
    this.departmentName = localStorage.getItem(STORAGE_KEYS.departmentName);
  }

  tryAgainRouteListener(retryFunction: () => void): void {
    this.router.events
      .pipe(
        takeUntil(this.subscribe$),
        filter((event: Event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        retryFunction();
      });
  }

  isHidden(): boolean {
    return !this.loading$ && !this.error$ && !this.modal$ && !this.backButton$;
  }

  backButtonClick(): void {
    const backButton: BackButton = this.store.get<BackButton>(STORE_FIELDS.backButton);
    this.store.add(STORE_FIELDS.backButton, null);

    if (backButton.returnUrl) {
      this.router.navigateByUrl(backButton.returnUrl);
      return;
    }
    const location: Location = this.injector.get(Location);
    location.back();
  }

  initBackButton(returnUrl?: string, buttonText?: string): void {
    this.store.add<Error>(STORE_FIELDS.error, null);
    this.store.add<BackButton>(STORE_FIELDS.backButton, new BackButton(returnUrl, buttonText));
  }

  toggleLoading(loading?: Loading): void {
    this.store.add<Loading>(STORE_FIELDS.loading, loading ? loading : null);
  }

  toggleError(errorMessage: Error, error?: HttpErrorResponse): void {
    this.store.add<Error>(STORE_FIELDS.error, this.errorService.buildError(errorMessage, error));
  }

  toggleFooter(isVisible: boolean): void {
    this.store.add<boolean>(STORE_FIELDS.footer, isVisible);
  }

  closeModal(): void {
    this.modalService.modalButtonMessage$.next('close');
    this.toggleModal(null);
  }

  toggleModal(modal: Modal, error?: HttpErrorResponse): void {
    const finalModal = error
      ? {
          ...modal,
          errorDetails: {
            correlationId: error?.headers?.get('Runpath-Correlation-Id') || '',
            timeStamp: error?.headers?.get('Date') || '',
          },
        }
      : modal;
    this.store.add<Modal>(STORE_FIELDS.modal, finalModal);
  }

  modalButtonClick(): void {
    this.modal$.pipe(take(1)).subscribe((modal) => {
      if (modal.buttonAction) {
        this.modalService.modalButtonMessage$.next(modal.buttonAction);
        return;
      }
      this.closeModal();
    });
  }

  getResolverData(): void {
    // Get stored data
    const department = this.store.get<Department>(STORE_FIELDS.department);
    if (department) {
      this.department = department;
    }

    // Get resolver data
    const activeRouter: ActivatedRoute = this.injector.get(ActivatedRoute);
    if (activeRouter.snapshot && activeRouter.snapshot.data.department) {
      this.department = activeRouter.snapshot.data.department;
    }
  }

  private getStoreState<T>(storeField: string): Observable<T> {
    return this.store.globalStateChanged.pipe(map((store: StoreState) => store && store[storeField]));
  }

  ngOnDestroy(): void {
    this.subscribe$.next(null);
    this.subscribe$.complete();
  }

  async sendCoverageCheckerAudit(): Promise<void> {
    this.departmentService.postDepartmentEvent(AuditEventsEnum.CoverageCheckerLinkClicked).toPromise();
  }

  addAppInsightsTelemetry(): void {
    const customProperties = new Map<string, string>();

    customProperties.set('DepartmentMode', this.department?.mode?.toString());
    customProperties.set('DepartmentShortName', this.department?.shortName);
    customProperties.set('IsOBFeatureOn', this.isOBFeatureOn?.toString());
    customProperties.set('IsWRFeatureOn', this.isWRFeatureOn?.toString());
    customProperties.set('DepartmentJourneyName', this.department?.journey?.name?.toString());
    customProperties.set('DepartmentShortName', this.department?.shortName);

    if (this.application) {
      customProperties.set('ApplicationReference', this.application.applicationReference);
    }

    this.injector.get(AppInsightsService).addTelemetry(customProperties);
  }

  get getWRCoverageCheckerURL(): string {
    return ApplicationConfigService.config.workReportCoverageCheckerUrl;
  }
}
